import { Plus, Eye, Globe } from 'lucide-react';
import { X, Headphones } from 'lucide-react';
import { FormSection } from './FormSection';
import { FormSection as FormSectionType, ProFormConfig } from '../../types/formBuilder';
import { createNewSection, createNewField } from '../../utils/formBuilderHelpers';
import { getPublicFalQueueWebhookFunctionUrl } from '../../utils/falWebhookUrl';

interface ModelConfig {
  modelName: string;
  description: string;
  category: string;
  subcategory?: string;
  tags: string;
  workflowPath: string;
  outputKey: string;
  outputType: string;
  iconName: string;
  iconColor: string;
  iconBg: string;
  creditCost: number;
  estimatedTime: string;
  route: string;
  sortOrder: number;
  isAvailable: boolean;
  comingSoon: boolean;
  featured: boolean;
  fal_stream_node_id: string;
  fal_polling_node_name: string;
  is_long_running: boolean;
  polling_interval_seconds: number;
  fal_client_mode: 'stream' | 'queue';
  /** Optional; empty uses project FAL queue webhook in Edge (with secret) */
  fal_queue_webhook_url: string;
}

type Mode = 'model' | 'pro-form';

interface FormBuilderProps {
  sections: FormSectionType[];
  onSectionsChange: (sections: FormSectionType[]) => void;
  onOpenPreview: () => void;
  onOpenFieldSettings: (sectionId: string, fieldId: string) => void;
  onOpenJsonAI: () => void;
  mode: Mode;
  onOpenPageSettings: () => void;
  modelConfig?: ModelConfig;
  onModelConfigChange?: (config: ModelConfig) => void;
  proFormConfig?: ProFormConfig;
  onProFormConfigChange?: (config: ProFormConfig) => void;
  onSaveConfiguration: () => void;
  isSaving?: boolean;
  saveError?: string | null;
  isEditMode?: boolean;
}

export const FormBuilder: React.FC<FormBuilderProps> = ({ 
  sections, 
  onSectionsChange, 
  onOpenPreview,
  onOpenFieldSettings,
  onOpenJsonAI,
  onOpenPageSettings,
  mode,
  modelConfig, // Only for model mode
  onModelConfigChange, // Only for model mode
  proFormConfig, // Only for pro-form mode
  onProFormConfigChange, // Only for pro-form mode
  onSaveConfiguration,
  isSaving = false,
  saveError,
  isEditMode = false,
}) => {
  const projectFalWebhook = getPublicFalQueueWebhookFunctionUrl();
  const streamNodeRequired =
    modelConfig && modelConfig.is_long_running && modelConfig.fal_client_mode !== 'queue';

  const addSection = () => {
    const newSection = createNewSection(sections.length);
    onSectionsChange([...sections, newSection]);
  };

  const removeSection = (sectionId: string) => {
    onSectionsChange(sections.filter(section => section.id !== sectionId));
  };

  const updateSectionTitle = (sectionId: string, title: string) => {
    onSectionsChange(sections.map(section => 
      section.id === sectionId ? { ...section, title } : section
    ));
  };

  const addField = (sectionId: string) => {
    onSectionsChange(sections.map(section => 
      section.id === sectionId 
        ? { ...section, fields: [...section.fields, createNewField()] }
        : section
    ));
  };

  const removeField = (sectionId: string, fieldId: string) => {
    onSectionsChange(sections.map(section => 
      section.id === sectionId 
        ? { ...section, fields: section.fields.filter(field => field.id !== fieldId) }
        : section
    ));
  };

  const updateField = (sectionId: string, fieldId: string, updates: Partial<FormSectionType['fields'][0]>) => {
    onSectionsChange(sections.map(section => 
      section.id === sectionId 
        ? {
            ...section,
            fields: section.fields.map(field => 
              field.id === fieldId ? { ...field, ...updates } : field
            )
          }
        : section
    ));
  };

  const moveFieldUp = (sectionId: string, fieldIndex: number) => {
    if (fieldIndex === 0) return; // Can't move up if already at top
    
    onSectionsChange(sections.map(section => 
      section.id === sectionId 
        ? {
            ...section,
            fields: section.fields.map((field, index) => {
              if (index === fieldIndex - 1) {
                // Move the field below up to current position
                return section.fields[fieldIndex];
              } else if (index === fieldIndex) {
                // Move current field to position above
                return section.fields[fieldIndex - 1];
              }
              return field;
            })
          }
        : section
    ));
  };

  const moveFieldDown = (sectionId: string, fieldIndex: number) => {
    const section = sections.find(s => s.id === sectionId);
    if (!section || fieldIndex === section.fields.length - 1) return; // Can't move down if already at bottom
    
    onSectionsChange(sections.map(section => 
      section.id === sectionId 
        ? {
            ...section,
            fields: section.fields.map((field, index) => {
              if (index === fieldIndex) {
                // Move current field to position below
                return section.fields[fieldIndex + 1];
              } else if (index === fieldIndex + 1) {
                // Move the field below to current position
                return section.fields[fieldIndex];
              }
              return field;
            })
          }
        : section
    ));
  };
  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="card p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-th-primary mb-2">
              Create Model Configuration
            </h2>
            <p className="text-th-secondary">
              Build dynamic forms for AI model configurations
            </p>
          </div>
          <div className="flex items-center space-x-3 flex-wrap gap-2">
            <button
              onClick={onOpenJsonAI}
              className="flex items-center space-x-2 px-4 py-2 bg-status-warning text-white rounded-lg font-medium hover:bg-status-warning/90 transition-all"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
              </svg>
              <span>JSON AI</span>
            </button>
            <button
              onClick={onOpenPreview}
              className="flex items-center space-x-2 px-4 py-2 bg-accent text-surface rounded-lg font-medium hover:bg-accent/90 transition-all"
            >
              <Eye className="w-4 h-4" />
              <span>Preview</span>
            </button>
            <button
              onClick={addSection}
              className="flex items-center space-x-2 px-4 py-2 bg-brand text-surface rounded-lg font-medium hover:bg-brand/90 transition-all"
            >
              <Plus className="w-4 h-4" />
              <span>Add Section</span>
            </button>
            {mode === 'model' && ( // Only show for model mode
              <button
                onClick={onOpenPageSettings}
                className="flex items-center space-x-2 px-4 py-2 bg-accent text-surface rounded-lg font-medium hover:bg-accent/90 transition-all"
              >
                <Globe className="w-4 h-4" />
                <span>Page Settings</span>
              </button>
            )}
          </div>
        </div>
        
        {/* Configuration Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6 pt-6 border-t border-subtle">
          {/* Model Name - Full Width */}
          <div className="md:col-span-2 space-y-2">
            <label className="block text-sm font-medium text-th-primary">{mode === 'model' ? 'Model Name' : 'Form Name'} *</label>
            {mode === 'model' && modelConfig && onModelConfigChange && (
              <input
              type="text"
              value={modelConfig.modelName}
              onChange={(e) => onModelConfigChange({ ...modelConfig, modelName: e.target.value })}
              placeholder="FLUX Pro Max"
              className="w-full bg-elevated border border-subtle rounded-lg px-4 py-3 text-th-primary placeholder-th-tertiary focus:border-brand focus:outline-none transition-all"
              required
            />
            )}
            {mode === 'pro-form' && proFormConfig && onProFormConfigChange && (
              <input
                type="text"
                value={proFormConfig.formName}
                onChange={(e) => onProFormConfigChange({ ...proFormConfig, formName: e.target.value })}
                placeholder="My Custom Request Form"
                className="w-full bg-elevated border border-subtle rounded-lg px-4 py-3 text-th-primary placeholder-th-tertiary focus:border-brand focus:outline-none transition-all"
                required
              />
            )}
            <p className="text-xs text-th-secondary">
              {mode === 'model' ? 'Display name for the AI model' : 'Display name for the pro request form'}
            </p>
          </div>

          {/* Description - Full Width */}
          <div className="md:col-span-2 space-y-2">
            <label className="block text-sm font-medium text-th-primary">{mode === 'model' ? 'Description' : 'Form Description'}</label>
            {mode === 'model' && modelConfig && onModelConfigChange && (
              <textarea
                value={modelConfig.description || ''}
                onChange={(e) => onModelConfigChange({ ...modelConfig, description: e.target.value })}
                placeholder="A detailed description of what this model does"
                rows={3}
                className="w-full bg-elevated border border-subtle rounded-lg px-4 py-3 text-th-primary placeholder-th-tertiary focus:border-brand focus:outline-none resize-none transition-all"
              />
            )}
            {mode === 'pro-form' && proFormConfig && onProFormConfigChange && (
              <textarea
                value={proFormConfig.formDescription || ''}
                onChange={(e) => onProFormConfigChange({ ...proFormConfig, formDescription: e.target.value })}
                placeholder="Describe the purpose of this request form"
                rows={3}
                className="w-full bg-elevated border border-subtle rounded-lg px-4 py-3 text-th-primary placeholder-th-tertiary focus:border-brand focus:outline-none resize-none transition-all"
              />
            )}
            <p className="text-xs text-th-secondary">
              {mode === 'model' ? 'Detailed description of the AI model' : 'Detailed description of the pro request form'}
            </p>
          </div>

          {mode === 'model' && modelConfig && onModelConfigChange && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-th-primary">Category *</label>
              <select
                value={modelConfig.category || 'models'}
                onChange={(e) => onModelConfigChange({ ...modelConfig, category: e.target.value })}
                className="w-full bg-elevated border border-subtle rounded-lg px-4 py-3 text-th-primary focus:border-brand focus:outline-none transition-all"
                required
              >
                <option value="models">Models</option>
                <option value="utils">Utils</option>
                <option value="effects">Effects</option>
              </select>
              <p className="text-xs text-th-secondary">Classification category for the AI model</p>
            </div>
          )}

          {/* Workflow Path */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-th-primary">
              Workflow Path *
            </label>
            {mode === 'model' && modelConfig && onModelConfigChange && (
              <input
                type="text"
                value={modelConfig.workflowPath}
                onChange={(e) => onModelConfigChange({ ...modelConfig, workflowPath: e.target.value })}
                placeholder="workflows/sanjuniperAI/your-workflow"
                className="w-full bg-elevated border border-subtle rounded-lg px-4 py-3 text-th-primary placeholder-th-tertiary focus:border-brand focus:outline-none transition-all"
                required
              />
            )}
            {mode === 'pro-form' && proFormConfig && onProFormConfigChange && (
              <input
              type="text"
              value={proFormConfig.routePath}
              onChange={(e) => onProFormConfigChange({ ...proFormConfig, routePath: e.target.value })}
              placeholder="workflows/sanjuniperAI/your-workflow"
              className="w-full bg-elevated border border-subtle rounded-lg px-4 py-3 text-th-primary placeholder-th-tertiary focus:border-brand focus:outline-none transition-all"
              required
            />
            )}
            <p className="text-xs text-th-secondary">
              {mode === 'model' ? 'FAL AI workflow endpoint path' : 'Public URL path for this form (e.g., /image-request)'}
            </p>
          </div>

          {mode === 'model' && modelConfig && onModelConfigChange && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-th-primary">Output Type *</label>
              <select
                value={modelConfig.outputType || 'image'}
                onChange={(e) => onModelConfigChange({ ...modelConfig, outputType: e.target.value })}
                className="w-full bg-elevated border border-subtle rounded-lg px-4 py-3 text-th-primary focus:border-brand focus:outline-none transition-all"
                required
              >
                <option value="image">Image</option>
                <option value="video">Video</option>
                <option value="audio">Audio</option>
              </select>
              <p className="text-xs text-th-secondary">Type of media output this model produces</p>
            </div>
          )}

          {mode === 'pro-form' && proFormConfig && onProFormConfigChange && (
            <>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-th-primary">Request Type *</label>
                <input
                  type="text"
                  value={proFormConfig.requestType}
                  onChange={(e) => onProFormConfigChange({ ...proFormConfig, requestType: e.target.value })}
                  placeholder="pro_image_generation"
                  className="w-full bg-elevated border border-subtle rounded-lg px-4 py-3 text-th-primary placeholder-th-tertiary focus:border-brand focus:outline-none transition-all"
                  required
                />
                <p className="text-xs text-th-secondary">
                  Internal identifier for this type of pro request (e.g., 'pro_image_generation')
                </p>
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-th-primary">App ID *</label>
                <input
                  type="text"
                  value={proFormConfig.appId}
                  onChange={(e) => onProFormConfigChange({ ...proFormConfig, appId: e.target.value })}
                  placeholder="Your Hyper App ID"
                  className="w-full bg-elevated border border-subtle rounded-lg px-4 py-3 text-th-primary placeholder-th-tertiary focus:border-brand focus:outline-none transition-all"
                  required
                />
                <p className="text-xs text-th-secondary">
                  Hyper App ID for authentication on this public pro page
                </p>
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-th-primary">Credit Cost *</label>
                <input
                  type="number"
                  min="1"
                  max="1000"
                  value={proFormConfig.creditCost || 10}
                  onChange={(e) => onProFormConfigChange({ ...proFormConfig, creditCost: parseInt(e.target.value) || 10 })}
                  placeholder="10"
                  className="w-full bg-elevated border border-subtle rounded-lg px-4 py-3 text-th-primary placeholder-th-tertiary focus:border-brand focus:outline-none transition-all"
                  required
                />
                <p className="text-xs text-th-secondary">
                  Number of credits required to submit this pro request
                </p>
              </div>
            </>
          )}

          {mode === 'model' && modelConfig && onModelConfigChange && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-th-primary">Credit Cost *</label>
              <input
              type="number"
              min="1"
              max="1000"
              value={modelConfig.creditCost}
              onChange={(e) => onModelConfigChange({ ...modelConfig, creditCost: parseInt(e.target.value) || 10 })}
              placeholder="10"
              className="w-full bg-elevated border border-subtle rounded-lg px-4 py-3 text-th-primary placeholder-th-tertiary focus:border-brand focus:outline-none transition-all"
              required
            />
            <p className="text-xs text-th-secondary">
              Number of credits required to use this model
            </p>
            </div>
          )}

          {mode === 'model' && modelConfig && onModelConfigChange && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-th-primary">Estimated Time *</label>
              <input
              type="text"
              value={modelConfig.estimatedTime || ''}
              onChange={(e) => onModelConfigChange({ ...modelConfig, estimatedTime: e.target.value })}
              placeholder="30-60s"
              className="w-full bg-elevated border border-subtle rounded-lg px-4 py-3 text-th-primary placeholder-th-tertiary focus:border-brand focus:outline-none transition-all"
              required
            />
            <p className="text-xs text-th-secondary">
              Estimated generation time (e.g., "30-60s", "2-4min")
            </p>
            </div>
          )}

          {mode === 'model' && modelConfig && onModelConfigChange && (
            <div className="md:col-span-2 space-y-4 p-4 bg-elevated/50 border border-subtle rounded-lg">
              <h4 className="text-th-primary font-semibold">FAL API</h4>
              <div className="inline-flex rounded-lg border border-subtle p-0.5 bg-elevated">
                <button
                  type="button"
                  onClick={() => onModelConfigChange({ ...modelConfig, fal_client_mode: 'stream' })}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    modelConfig.fal_client_mode === 'stream'
                      ? 'bg-brand text-surface'
                      : 'text-th-secondary hover:text-th-primary'
                  }`}
                >
                  Stream
                </button>
                <button
                  type="button"
                  onClick={() => onModelConfigChange({ ...modelConfig, fal_client_mode: 'queue' })}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    modelConfig.fal_client_mode === 'queue'
                      ? 'bg-brand text-surface'
                      : 'text-th-secondary hover:text-th-primary'
                  }`}
                >
                  Queue
                </button>
              </div>
              <p className="text-xs text-th-secondary">
                {modelConfig.fal_client_mode === 'stream'
                  ? 'Uses fal.stream for fast and long-running flows. For background jobs, capture request_id from the stream submit event matching your Stream Node ID.'
                  : 'Uses fal.queue.submit. Pass webhookUrl to FAL; leave override empty to use the project callback URL below (Edge adds the secret server-side).'}
              </p>
              {modelConfig.fal_client_mode === 'queue' && (
                <div className="space-y-3">
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-th-primary">Project FAL queue webhook (read-only)</label>
                    <input
                      type="text"
                      readOnly
                      value={projectFalWebhook || '— (set VITE_SUPABASE_URL)'}
                      className="w-full bg-muted/30 border border-subtle rounded-lg px-4 py-2 text-th-secondary font-mono text-xs"
                    />
                    <p className="text-xs text-th-secondary">
                      Deploy <code className="text-th-primary">fal-queue-webhook</code> and set <code className="text-th-primary">FAL_WEBHOOK_SECRET</code> in Supabase; Edge will append the token to the default webhook URL.
                    </p>
                  </div>
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-th-primary">Optional webhook URL override</label>
                    <input
                      type="url"
                      value={modelConfig.fal_queue_webhook_url}
                      onChange={(e) =>
                        onModelConfigChange({ ...modelConfig, fal_queue_webhook_url: e.target.value })
                      }
                      placeholder="Leave empty to use the project callback"
                      className="w-full bg-elevated border border-subtle rounded-lg px-4 py-3 text-th-primary placeholder-th-tertiary focus:border-brand focus:outline-none"
                    />
                    <p className="text-xs text-th-secondary">
                      Override only if this workflow must post to a different callback than the project default.
                    </p>
                  </div>
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-th-primary">FAL Polling Node Name *</label>
                    <input
                      type="text"
                      value={modelConfig.fal_polling_node_name}
                      onChange={(e) => onModelConfigChange({ ...modelConfig, fal_polling_node_name: e.target.value })}
                      placeholder="e.g. nano-banana-2/edit (no fal-ai/ prefix)"
                      className="w-full bg-elevated border border-subtle rounded-lg px-4 py-3 text-th-primary placeholder-th-tertiary focus:border-brand focus:outline-none font-mono text-sm"
                    />
                    <p className="text-xs text-th-secondary">
                      Required for <code className="text-th-primary">queue.fal.run/.../requests/&#123;id&#125;/status</code>. Use the path segment after{' '}
                      <code className="text-th-primary">fal-ai/</code> in your queue URL. If empty, the Edge function tries to derive this from your FAL endpoint.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Background Processing Section - Only for model mode */}
          {mode === 'model' && modelConfig && onModelConfigChange && (
            <div className="md:col-span-2 space-y-6 p-6 bg-status-warning/5 border border-status-warning/20 rounded-lg">
              <h4 className="text-lg font-semibold text-th-primary mb-4 flex items-center space-x-2">
                <svg className="w-5 h-5 text-status-warning" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                </svg>
                <span>Background Processing Configuration</span>
              </h4>
              
              <div className="space-y-4">
                {/* Long Running Toggle */}
                <div className="space-y-2">
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={modelConfig.is_long_running}
                      onChange={(e) => onModelConfigChange({ ...modelConfig, is_long_running: e.target.checked })}
                      className="w-4 h-4 text-status-warning bg-elevated border-subtle rounded focus:ring-status-warning focus:ring-2"
                    />
                    <div>
                      <span className="text-th-primary font-medium">Enable Background Processing</span>
                      <p className="text-th-secondary text-xs">For models that take longer than 2 minutes to generate</p>
                    </div>
                  </label>
                </div>

                {/* Show additional fields only when long running is enabled */}
                {modelConfig.is_long_running && (
                  <>
                    {modelConfig.fal_client_mode === 'queue' && (
                      <p className="text-sm text-th-secondary bg-elevated/80 border border-subtle rounded-lg p-3">
                        <strong className="text-th-primary">Queue + background:</strong> the <code className="text-status-warning">request_id</code> comes
                        from <code className="text-status-warning">fal.queue.submit</code> (no stream <code>node_id</code>).
                        Polling node name is still used for <code className="text-status-warning">queue.fal.run/.../requests/&#123;id&#125;</code> and the polling worker; completion webhooks to{' '}
                        <code className="text-th-primary">fal-queue-webhook</code> are optional but recommended.
                      </p>
                    )}
                    <div
                      className={`grid grid-cols-1 gap-4 ${modelConfig.fal_client_mode !== 'queue' ? 'md:grid-cols-2' : ''}`}
                    >
                      {/* FAL Stream Node ID */}
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-th-primary">
                          FAL Stream Node ID{streamNodeRequired ? ' *' : ''}
                        </label>
                        <input
                          type="text"
                          value={modelConfig.fal_stream_node_id}
                          onChange={(e) => onModelConfigChange({ ...modelConfig, fal_stream_node_id: e.target.value })}
                          placeholder="fal_ai/flux_pro/kontext"
                          className="w-full bg-elevated border border-subtle rounded-lg px-4 py-3 text-th-primary placeholder-th-tertiary focus:border-status-warning focus:outline-none transition-all font-mono text-sm"
                          required={!!streamNodeRequired}
                        />
                        <p className="text-xs text-th-secondary">
                          {streamNodeRequired
                            ? 'Exact node_id from FAL stream submit event to capture request_id'
                            : 'Not used for queue mode; optional for reference'}
                        </p>
                      </div>

                      {/* FAL Polling Node Name — also under FAL API → Queue for fast queue runs */}
                      {modelConfig.fal_client_mode !== 'queue' && (
                        <div className="space-y-2">
                          <label className="block text-sm font-medium text-th-primary">
                            FAL Polling Node Name *
                          </label>
                          <input
                            type="text"
                            value={modelConfig.fal_polling_node_name}
                            onChange={(e) => onModelConfigChange({ ...modelConfig, fal_polling_node_name: e.target.value })}
                            placeholder="flux_pro/kontext"
                            className="w-full bg-elevated border border-subtle rounded-lg px-4 py-3 text-th-primary placeholder-th-tertiary focus:border-status-warning focus:outline-none transition-all font-mono text-sm"
                            required={modelConfig.is_long_running}
                          />
                          <p className="text-xs text-th-secondary">
                            Node name for FAL queue API URLs (without fal-ai/ prefix) — used by workers and webhooks
                          </p>
                        </div>
                      )}
                    </div>
                    {modelConfig.fal_client_mode === 'queue' && (
                      <p className="text-xs text-th-secondary">
                        For queue mode, set <strong className="text-th-primary">FAL Polling Node Name</strong> in the{' '}
                        <strong className="text-th-primary">FAL API</strong> section above (or rely on endpoint auto-derivation).
                      </p>
                    )}

                    {/* Polling Interval */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-th-primary">
                        Polling Interval (seconds)
                      </label>
                      <input
                        type="number"
                        min="10"
                        max="3600"
                        value={modelConfig.polling_interval_seconds}
                        onChange={(e) => onModelConfigChange({ ...modelConfig, polling_interval_seconds: parseInt(e.target.value) || 60 })}
                        placeholder="60"
                        className="w-full bg-elevated border border-subtle rounded-lg px-4 py-3 text-th-primary placeholder-th-tertiary focus:border-status-warning focus:outline-none transition-all"
                      />
                      <p className="text-xs text-th-secondary">
                        How often to check generation status (10-3600 seconds)
                      </p>
                    </div>

                    {/* Example Configuration */}
                    <div className="bg-status-warning/10 border border-status-warning/30 rounded-lg p-4">
                      <h5 className="text-status-warning font-medium text-sm mb-2">💡 Configuration example</h5>
                      {modelConfig.fal_client_mode === 'stream' ? (
                        <div className="space-y-2 text-xs text-th-secondary">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                              <span className="text-th-primary font-medium">Stream Node ID:</span>
                              <code className="text-status-warning font-mono ml-2">fal_ai/flux_pro/kontext</code>
                            </div>
                            <div>
                              <span className="text-th-primary font-medium">Polling Node Name:</span>
                              <code className="text-status-warning font-mono ml-2">flux_pro/kontext</code>
                            </div>
                          </div>
                          <p className="pt-2 border-t border-status-warning/20">
                            The Stream Node ID matches the FAL stream <code>submit</code> event; the Polling Node Name builds{' '}
                            <code className="text-status-warning font-mono">https://queue.fal.run/fal-ai/{modelConfig.fal_polling_node_name || 'polling_node_name'}/requests/{'{request_id}'}/status</code>
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-2 text-xs text-th-secondary">
                          <p className="text-th-primary font-medium">Queue submit (paste-style)</p>
                          <pre className="whitespace-pre-wrap break-all bg-elevated/60 rounded p-2 font-mono text-[11px] text-status-warning">
{`const { request_id } = await fal.queue.submit("workflows/your/endpoint", {
  input: { /* ... */ },
  webhookUrl: "<project or override URL with token>"
});
// Polling: https://queue.fal.run/fal-ai/${modelConfig.fal_polling_node_name || 'your_polling_node'}/requests/{request_id}/status`}
                          </pre>
                          <p className="pt-2 border-t border-status-warning/20">
                            Use <strong className="text-th-primary">FAL Polling Node Name</strong> (same as stream flows) for HTTP status/result URLs. Webhook + polling worker are both supported.
                          </p>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Save Button */}
          <div className="md:col-span-2">
            {/* Example Media for Pro Forms */}
            {mode === 'pro-form' && proFormConfig && onProFormConfigChange && (
              <div className="space-y-6 mb-6 p-4 bg-status-warning/5 border border-status-warning/20 rounded-lg">
                <h4 className="text-lg font-semibold text-th-primary mb-4">Example Media Preview (Optional)</h4>
                
                <div className="grid grid-cols-1 gap-4">
                  {/* Example Media URL Input */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-th-primary">
                      Example Media URL
                    </label>
                    <div className="flex items-center space-x-3">
                      <input
                        type="url"
                        value={proFormConfig.exampleMediaUrl || ''}
                        onChange={(e) => {
                          const url = e.target.value.trim();
                          let mediaType: 'image' | 'video' | 'audio' | undefined;
                          
                          if (url) {
                            const extension = url.split('.').pop()?.toLowerCase() || '';
                            if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm'].includes(extension)) {
                              mediaType = 'video';
                            } else if (['mp3', 'wav', 'flac', 'aac', 'ogg', 'm4a'].includes(extension)) {
                              mediaType = 'audio';
                            } else {
                              mediaType = 'image';
                            }
                          }
                          
                          onProFormConfigChange({ 
                            ...proFormConfig, 
                            exampleMediaUrl: url,
                            exampleMediaType: mediaType
                          });
                        }}
                        placeholder="https://example.com/image.jpg or video.mp4 or audio.mp3"
                        className="flex-1 bg-elevated border border-subtle rounded-lg px-4 py-3 text-th-primary placeholder-th-tertiary focus:border-brand focus:outline-none transition-all"
                      />
                      {proFormConfig.exampleMediaUrl && (
                        <button
                          type="button"
                          onClick={() => onProFormConfigChange({ 
                            ...proFormConfig, 
                            exampleMediaUrl: '',
                            exampleMediaType: undefined
                          })}
                          className="p-3 text-status-error hover:text-status-error/80 bg-status-error/10 border border-status-error/30 rounded-lg transition-all"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    <p className="text-xs text-th-secondary">
                      Direct URL to an image, video, or audio file to showcase on the public pro page
                    </p>
                  </div>

                  {/* Live Media Preview */}
                  {proFormConfig.exampleMediaUrl && (
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-th-primary">
                        Live Preview
                      </label>
                      <div className="bg-elevated border border-subtle rounded-lg p-4">
                        {proFormConfig.exampleMediaType === 'image' ? (
                          <div className="w-full max-w-sm mx-auto">
                            <img
                              src={proFormConfig.exampleMediaUrl}
                              alt="Example preview"
                              className="w-full h-auto max-h-48 object-contain rounded-lg"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                                const parent = target.parentElement;
                                if (parent) {
                                  parent.innerHTML = '<div class="text-center text-status-error p-4"><svg class="w-8 h-8 mx-auto mb-2" fill="currentColor" viewBox="0 0 24 24"><path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/></svg><p class="text-sm">Failed to load image</p></div>';
                                }
                              }}
                            />
                          </div>
                        ) : proFormConfig.exampleMediaType === 'video' ? (
                          <div className="w-full max-w-sm mx-auto">
                            <video
                              src={proFormConfig.exampleMediaUrl}
                              className="w-full h-auto max-h-48 object-contain rounded-lg"
                              controls
                              preload="metadata"
                              onError={(e) => {
                                const target = e.target as HTMLVideoElement;
                                target.style.display = 'none';
                                const parent = target.parentElement;
                                if (parent) {
                                  parent.innerHTML = '<div class="text-center text-status-error p-4"><svg class="w-8 h-8 mx-auto mb-2" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg><p class="text-sm">Failed to load video</p></div>';
                                }
                              }}
                            />
                          </div>
                        ) : proFormConfig.exampleMediaType === 'audio' ? (
                          <div className="w-full max-w-sm mx-auto text-center space-y-4">
                            <div className="w-16 h-16 bg-status-warning/20 rounded-full flex items-center justify-center mx-auto">
                              <Headphones className="w-8 h-8 text-status-warning" />
                            </div>
                            <audio
                              src={proFormConfig.exampleMediaUrl}
                              className="w-full bg-elevated rounded-lg"
                              controls
                              preload="metadata"
                              onError={(e) => {
                                const target = e.target as HTMLAudioElement;
                                target.style.display = 'none';
                                const parent = target.parentElement;
                                if (parent) {
                                  parent.innerHTML = '<div class="text-center text-status-error"><p class="text-sm">Failed to load audio</p></div>';
                                }
                              }}
                            />
                          </div>
                        ) : null}
                        
                        {proFormConfig.exampleMediaType && (
                          <div className="mt-3 text-center">
                            <span className="text-xs px-2 py-1 bg-status-success/20 text-status-success rounded-full font-medium">
                              {proFormConfig.exampleMediaType} • Auto-detected
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            
          <button 
            onClick={onSaveConfiguration}
            disabled={isSaving}
            className="w-full px-6 py-3 bg-brand text-surface rounded-lg font-medium hover:bg-brand/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? 'Saving...' : isEditMode ? 'Update Configuration' : 'Save Configuration'}
          </button>
          </div>
        </div>
        
        {/* Save Error Display */}
        {saveError && (
          <div className="mt-4 p-4 bg-overlay border border-subtle rounded-lg">
            <p className="text-status-error text-sm">{saveError}</p>
          </div>
        )}
      </div>

      {/* Form Sections */}
      <div className="space-y-6">
        {sections.map((section) => (
          <FormSection
            key={section.id}
            section={section}
            canRemove={sections.length > 1}
            onUpdateTitle={updateSectionTitle}
            onAddField={addField}
            onRemoveSection={removeSection}
            onUpdateField={updateField}
            onRemoveField={removeField}
            onOpenFieldSettings={onOpenFieldSettings}
            onMoveFieldUp={moveFieldUp}
            onMoveFieldDown={moveFieldDown}
          />
        ))}
      </div>

    </div>
  );
};